import { useEffect, useRef, useState } from 'react';
import { AlertOctagon, CornerDownLeft, X } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { SOURCE_META, useNotifications, type AppAlert } from '@/store/notifications';
import { cn } from '@/lib/utils';

/**
 * Subscribes to the notifications store and pops a centred amber/red flash
 * whenever a critical alert arrives. Auto-dismisses after a few seconds
 * unless the operator interacts. Stays out of the way for warn / info —
 * those show only as the bell badge.
 *
 * Visual: a top-centre "respond now" card that pulses on entry and slides
 * out on dismiss. Has Acknowledge + a per-source mute-15-minutes option.
 */
export function CriticalAlertFlash() {
  const alerts = useNotifications((s) => s.alerts);
  const markRead = useNotifications((s) => s.markRead);
  const mute = useNotifications((s) => s.mute);
  const [showing, setShowing] = useState<AppAlert | null>(null);
  const seenRef = useRef<Set<string>>(new Set());
  const timerRef = useRef<number | null>(null);

  // Watch for newly-arrived critical alerts.
  useEffect(() => {
    const critical = Object.values(alerts)
      .filter((a) => a.severity === 'critical' && !a.read)
      .sort((a, b) => b.ts - a.ts);
    for (const a of critical) {
      if (!seenRef.current.has(a.id)) {
        seenRef.current.add(a.id);
        setShowing(a);
        if (timerRef.current) window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => setShowing(null), 12_000);
        break;
      }
    }
  }, [alerts]);

  // Initialise the seenRef with anything already read so the first render
  // doesn't replay historical alerts.
  useEffect(() => {
    seenRef.current = new Set(
      Object.values(alerts)
        .filter((a) => a.read || a.severity !== 'critical')
        .map((a) => a.id),
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!showing) return null;

  const source = SOURCE_META[showing.source];

  function dismiss() {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    markRead(showing!.id);
    setShowing(null);
  }
  function muteSource() {
    mute(showing!.source, 15 * 60 * 1000);
    dismiss();
  }

  return (
    <div
      role="alert"
      className={cn(
        'fixed left-1/2 top-[68px] z-[57] w-[min(560px,92vw)] -translate-x-1/2',
        'animate-critical-flash',
      )}
    >
      <div className="overflow-hidden rounded-lg border-2 border-status-critical/70 bg-surface-1 shadow-[0_0_40px_rgba(239,68,68,0.45)]">
        <div className="flex items-center gap-2 border-b border-status-critical/40 bg-status-critical/15 px-3 py-2">
          <div className="grid h-7 w-7 place-items-center rounded bg-status-critical/30 text-status-critical">
            <AlertOctagon className="h-4 w-4" />
          </div>
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-status-critical">
            critical alert
          </span>
          <Badge tone={source.tone}>{source.label}</Badge>
          {showing.robot_id && (
            <span className="font-mono text-[10.5px] text-fg-muted">{showing.robot_id}</span>
          )}
          <button
            type="button"
            onClick={dismiss}
            className="ml-auto grid h-6 w-6 place-items-center rounded text-fg-secondary hover:bg-status-critical/20 hover:text-status-critical"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="p-3">
          <h3 className="text-[14px] font-semibold leading-tight text-fg-primary">
            {showing.title}
          </h3>
          <p className="mt-0.5 text-[12px] leading-snug text-fg-secondary">{showing.body}</p>
          <div className="mt-3 flex items-center gap-2">
            {showing.action && (
              <Button size="sm">
                <CornerDownLeft className="h-3.5 w-3.5" />
                {showing.action.label}
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={dismiss}>
              Acknowledge
            </Button>
            <button
              type="button"
              onClick={muteSource}
              className="ml-auto text-[10.5px] text-fg-muted hover:text-fg-secondary"
              title={`Silence ${source.label} alerts for 15 min`}
            >
              Mute {source.label.toLowerCase()} for 15 min
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
